import styles from 'css/src/components/app/template-preview/TemplatePreviewModal.module.scss';
import shellStyles from 'css/src/components/common/PreviewModalShell.module.scss';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Immutable from 'immutable';
import classNames from 'classnames';
import type { TemplateColor, TemplateColorProperties } from '@lumen5/luminary';
import { AspectRatio } from '@lumen5/luminary';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';

import type { Template } from 'js/src/client';
import AspectRatioPicker from 'js/src/components/app/AspectRatioPicker';
import TemplateScenePreviews from 'js/src/components/app/template-preview/TemplateScenePreviews';
import BrandKitDropdown from 'js/src/components/common/BrandKitDropdown';
import ColorPaletteTile from 'js/src/components/common/ColorPaletteTile';
import PreviewModalShell from 'js/src/components/common/PreviewModalShell';
import { Toast } from 'js/src/components/common/Toast';
import {
    BUTTON_WIDTHS,
    PrimaryButton,
    PrimaryTextButton,
    SPINNER_ICON,
} from 'js/src/components/common/ui/Button';
import DashboardSketchInput from 'js/src/components/dashboard/DashboardSketchInput';
import VideoPlayer from 'js/src/components/public-pages/common-sections/VideoPlayer';
import { getIsUserAllowedAccessToAccessBlueprintsAndBrandKits } from 'js/src/data/account';
import {
    getCanUseAIScript,
    getCanUseOutlineAnIdea,
    getCanUseSketch,
} from 'js/src/data/account/account-selectors';
import { getBrandKitIdList, loadBrandKits } from 'js/src/data/brand-kits';
import { saveSketchControlsToCookie } from 'js/src/data/sketch-controls/sketch-controls-cookies';
import {
    clearBrandKit as clearSketchBrandKit,
    clearTemplate as clearSketchTemplate,
    setAspectRatio as setSketchAspectRatio,
    setBrandKit as setSketchBrandKit,
    setTemplate as setSketchTemplate,
} from 'js/src/data/sketch-controls/sketch-controls-slice';
import { clearIncompatibleSketchControls } from 'js/src/data/sketch-controls/sketch-controls-validation';
import { getSupportedAspectRatios, getTemplateColorPalettes } from 'js/src/data/templates';
import type { PaletteData } from 'js/src/data/templates/templates-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { NEW_VIDEO_FLOW_CREATION_METHOD } from 'js/src/libs/consts';
import { int2hex } from 'js/src/libs/css-helpers';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { useIsHydrated } from 'js/src/libs/use-is-hydrated';

const IMAGE_PREVIEW = 'is_image';
const VIDEO_PREVIEW = 'is_video';

function ScenePreviewsErrorFallback() {
    return (
        <div className="d-flex align-items-center justify-content-center text-muted">
            <div className="text-center">
                <i className="material-symbols-outlined mb-l5-8">error_outline</i>
                <p className="mb-l5-0">Unable to load scene previews</p>
            </div>
        </div>
    );
}

export enum TEMPLATE_PREVIEW_VARIANTS {
    DEFAULT = 'default',
    HOME = 'home',
    PUBLIC = 'public',
}

type TemplatePreviewModalProps = {
    isVisible: boolean;
    template: ImmutableMap<Template> | null;
    onTemplateSelection: (
        selectedAspectRatio: AspectRatio,
        selectedPaletteIndex: number | null,
        selectedBrandKitId: number | null,
    ) => void;
    onClose: () => void;
    variant?: TEMPLATE_PREVIEW_VARIANTS;
    /**
     * Render on the page instead of in modal chrome, for surfaces where the template
     * being previewed is what the page is about. `onClose` is never called in this mode.
     */
    inline?: boolean;
    onSketchActivated?: (templateId: string, selectedAspectRatio: AspectRatio) => void;
    /**
     * Optional handler invoked when the user chooses to skip the script flow
     * entirely and open the editor directly with the selected template.
     */
    onStartFromScratch?: (
        selectedAspectRatio: AspectRatio,
        selectedPaletteIndex: number | null,
        selectedBrandKitId: number | null,
    ) => void;
};

const TemplatePreviewModal = React.memo(function TemplatePreviewModal({
    isVisible,
    template,
    onTemplateSelection,
    onClose,
    variant = TEMPLATE_PREVIEW_VARIANTS.DEFAULT,
    inline,
    onSketchActivated,
    onStartFromScratch,
}: TemplatePreviewModalProps) {
    const isHydrated = useIsHydrated();
    const isHomeVariant = variant === TEMPLATE_PREVIEW_VARIANTS.HOME;
    const isPublicVariant = variant === TEMPLATE_PREVIEW_VARIANTS.PUBLIC;
    const dispatch = useDispatch<AppDispatch>();
    const canUseSketch = useSelector(getCanUseSketch);
    const canUseOutlineAnIdea = useSelector(getCanUseOutlineAnIdea);
    const canUseAIScript = useSelector(getCanUseAIScript) ?? true;
    const [templatePreviewUrl, setTemplatePreviewUrl] = React.useState<null | string>(null);
    const [previewNodeType, setPreviewNodeType] = React.useState(VIDEO_PREVIEW);
    const [isProceeding, setIsProceeding] = useState(false);
    const [selectedPaletteIndex, setSelectedPaletteIndex] = React.useState<number | null>(null);
    const [selectedBrandKitId, setSelectedBrandKitId] = React.useState<number | null>(null);
    const [showAllScenes, setShowAllScenes] = React.useState(false);

    // getIsUserAllowedAccessToAccessBlueprintsAndBrandKits falls back to a cookie while
    // workspaces load, so a logged-out visitor who was signed in previously would
    // otherwise still request brand kits and get a 403.
    const hasAccessToBrandKits =
        useSelector(getIsUserAllowedAccessToAccessBlueprintsAndBrandKits) && !isPublicVariant;
    const brandKitIds = useSelector(getBrandKitIdList);

    useEffect(() => {
        if (isVisible && hasAccessToBrandKits) {
            dispatch(loadBrandKits());
        }
    }, [dispatch, isVisible, hasAccessToBrandKits]);

    const templateExists = template && !template.isEmpty();
    const templateName = templateExists ? template.get('display_name') : null;
    const templateId = templateExists ? template.get('id') : null;
    // Authored to help the AI pick a template, so it only earns its space on the public
    // themes pages, where visitors are browsing rather than working.
    const templateDescription =
        isPublicVariant && templateExists ? template.get('description') : null;

    const defaultAspectRatio = template?.get('default_aspect_ratio') as AspectRatio | null;
    const supportedAspectRatios = useSelector((state: RootState) =>
        getSupportedAspectRatios(state, templateId),
    );

    const getInitialAspectRatio = React.useCallback(() => {
        if (defaultAspectRatio && supportedAspectRatios.includes(defaultAspectRatio)) {
            return defaultAspectRatio;
        }
        return supportedAspectRatios[0] || AspectRatio.LANDSCAPE;
    }, [defaultAspectRatio, supportedAspectRatios]);

    const [selectedAspectRatio, setSelectedAspectRatio] = React.useState(getInitialAspectRatio());

    const palettes = useSelector((state: RootState) =>
        templateId ? getTemplateColorPalettes(state, templateId) : Immutable.List(),
    );

    React.useEffect(() => {
        if (templateExists) {
            setSelectedAspectRatio(getInitialAspectRatio());
        }
    }, [templateExists, getInitialAspectRatio]);

    React.useEffect(() => {
        if (palettes.size > 0 && selectedPaletteIndex === null && selectedBrandKitId === null) {
            setSelectedPaletteIndex(0);
        }
    }, [palettes, selectedPaletteIndex, selectedBrandKitId]);

    // Keep sketch-controls in sync with the live selections so the embedded
    // sketch input (and the destination sketch page) reflect the user's
    // template, aspect ratio, and brand kit choices.
    React.useEffect(() => {
        if (!isHomeVariant || !isVisible || !templateId || !template) return;
        dispatch(setSketchTemplate(templateId));
        dispatch(
            clearIncompatibleSketchControls(
                { type: 'template', newValue: template },
                { showToast: false },
            ),
        );
        dispatch(setSketchAspectRatio(selectedAspectRatio));
        dispatch(
            clearIncompatibleSketchControls(
                { type: 'aspectRatio', newValue: selectedAspectRatio },
                { showToast: false },
            ),
        );
        if (selectedBrandKitId !== null) {
            dispatch(setSketchBrandKit(selectedBrandKitId));
        } else {
            dispatch(clearSketchBrandKit());
        }
    }, [
        dispatch,
        isHomeVariant,
        isVisible,
        templateId,
        template,
        selectedAspectRatio,
        selectedBrandKitId,
    ]);

    React.useEffect(() => {
        let previewUrl: string | null = null;
        if (templateExists) {
            // For soon to be deprecated templates, we use the [format]_mp4_url fields to store the correct aspect ratio image urls.
            previewUrl = template.get('landscape_mp4_url');
            const imageRegex = new RegExp('(gif|jpg|jpeg|tiff|png|webp)$', 'i');
            if (previewUrl && imageRegex.test(previewUrl)) {
                setPreviewNodeType(IMAGE_PREVIEW);
            } else {
                setPreviewNodeType(VIDEO_PREVIEW);
            }
        }
        setTemplatePreviewUrl(previewUrl);
    }, [template, templateExists]);

    const getPreviewUrlForAspectRatio = React.useCallback(
        (aspectRatio: AspectRatio): string | null => {
            if (!templateExists) return null;
            const fieldName = `${aspectRatio}_mp4_url` as keyof Template;
            return template.get(fieldName) as string | null;
        },
        [template, templateExists],
    );

    React.useEffect(() => {
        const playbackUrl = getPreviewUrlForAspectRatio(selectedAspectRatio);
        setTemplatePreviewUrl(playbackUrl);
    }, [selectedAspectRatio, getPreviewUrlForAspectRatio]);

    const handleUserSelection = React.useCallback(() => {
        setIsProceeding(true);
        onTemplateSelection(selectedAspectRatio, selectedPaletteIndex, selectedBrandKitId);
    }, [onTemplateSelection, selectedAspectRatio, selectedPaletteIndex, selectedBrandKitId]);

    const handleSketchBeforeNavigate = React.useCallback(() => {
        if (!templateId) return;
        // Persist sketch-controls to cookie so the destination sketch page
        // picks up the user's selections after the full-page navigation.
        dispatch(saveSketchControlsToCookie());
        analytics.track('Select template via modal', {
            method: NEW_VIDEO_FLOW_CREATION_METHOD.TEMPLATE_PREVIEW_MODAL,
            themeId: templateId,
            aspectRatio: selectedAspectRatio,
            paletteIndex: selectedPaletteIndex,
            brandKitId: selectedBrandKitId,
            entry: 'sketch',
        });
        onSketchActivated?.(templateId, selectedAspectRatio);
    }, [
        dispatch,
        templateId,
        selectedAspectRatio,
        selectedPaletteIndex,
        selectedBrandKitId,
        onSketchActivated,
    ]);

    const handlePaletteClick = React.useCallback(
        (paletteName: string) => {
            const paletteIndex = (palettes.toArray() as ImmutableMap<PaletteData>[]).findIndex(
                (p) => p.get('name') === paletteName,
            );
            if (paletteIndex !== -1) {
                setSelectedPaletteIndex(paletteIndex);
                setSelectedBrandKitId(null);
            }
        },
        [palettes],
    );

    const handleBrandKitClick = React.useCallback((brandKitId: number) => {
        setSelectedBrandKitId(brandKitId);
        setSelectedPaletteIndex(null);
    }, []);

    const onAspectRatioChange = React.useCallback(
        (aspectRatio: AspectRatio) => {
            const playbackUrl = getPreviewUrlForAspectRatio(aspectRatio);
            setTemplatePreviewUrl(playbackUrl);
            setSelectedAspectRatio(aspectRatio);
        },
        [getPreviewUrlForAspectRatio],
    );

    const handleStartFromScratch = React.useCallback(() => {
        if (!onStartFromScratch || !templateId) return;
        analytics.track('Select template via modal', {
            method: NEW_VIDEO_FLOW_CREATION_METHOD.TEMPLATE_PREVIEW_MODAL,
            themeId: templateId,
            aspectRatio: selectedAspectRatio,
            paletteIndex: selectedPaletteIndex,
            brandKitId: selectedBrandKitId,
            entry: 'start_from_scratch',
        });
        setIsProceeding(true);
        onStartFromScratch(selectedAspectRatio, selectedPaletteIndex, selectedBrandKitId);
    }, [
        onStartFromScratch,
        templateId,
        selectedAspectRatio,
        selectedPaletteIndex,
        selectedBrandKitId,
    ]);

    const handleModalOnClose = React.useCallback(() => {
        onClose();
        setSelectedAspectRatio(AspectRatio.LANDSCAPE);
        setTemplatePreviewUrl(null);
        setSelectedPaletteIndex(null);
        setSelectedBrandKitId(null);
        setShowAllScenes(false);
        if (isHomeVariant) {
            dispatch(clearSketchTemplate());
            dispatch(clearSketchBrandKit());
        }
    }, [onClose, isHomeVariant, dispatch]);

    if (!template || template.isEmpty()) {
        Toast.error('Not a valid selection. Please select a valid template to see a preview.');
        return null;
    }

    let previewNode = null;

    switch (previewNodeType) {
        case IMAGE_PREVIEW:
            if (!templatePreviewUrl)
                throw new Error('Template preview url is required for templates');
            previewNode = (
                <div className={styles['preview-container']}>
                    <img
                        src={templatePreviewUrl}
                        alt="template preview"
                        className={classNames(
                            styles['template-preview-image'],
                            styles['default-preview-image'],
                            selectedAspectRatio,
                        )}
                    />
                </div>
            );
            break;
        case VIDEO_PREVIEW:
            if (!templatePreviewUrl) {
                previewNode = null;
            } else {
                previewNode = (
                    <div className={styles['preview-container']}>
                        <VideoPlayer
                            videoUrl={templatePreviewUrl}
                            autoPlay={true}
                            muted={true}
                            loop={true}
                            classNames="template-preview-video"
                        />
                    </div>
                );
            }
            break;
    }

    const formatSection = (
        <div className={shellStyles['section']}>
            <div className={shellStyles['section-label']}>Format</div>
            <AspectRatioPicker
                templateId={templateId}
                selectedAspectRatio={selectedAspectRatio}
                onAspectRatioChange={onAspectRatioChange}
            />
        </div>
    );

    const standardFooter = (
        <div className="d-flex align-items-center justify-content-end w-100">
            <PrimaryButton
                onClick={handleUserSelection}
                text={isPublicVariant ? 'Sign up free to use this theme' : 'Use this template'}
                disabled={isProceeding}
                iconLeft={isProceeding ? SPINNER_ICON : null}
            />
        </div>
    );

    const previewBlock = (
        <div className={`${styles['preview-node-wrapper']} mb-l5-16`}>
            {previewNode}
            {((selectedPaletteIndex !== null && selectedPaletteIndex !== 0 && palettes.size > 0) ||
                selectedBrandKitId !== null) && (
                <div className={`${styles['palette-warning-overlay']} text-center`}>
                    <span className={styles['palette-warning-message']}>
                        Video preview does not reflect
                        {selectedBrandKitId !== null ? ' brand kit' : ' color palette'} selected
                    </span>
                </div>
            )}
        </div>
    );

    const brandKitSection = hasAccessToBrandKits && brandKitIds.size > 0 && (
        <div className={shellStyles['section']}>
            <div className={shellStyles['section-label']}>Brand kit (optional)</div>
            <BrandKitDropdown
                brandKitIds={brandKitIds}
                selectedBrandKitId={selectedBrandKitId}
                onSelect={(brandKitId) => {
                    if (brandKitId == null) {
                        setSelectedBrandKitId(null);
                    } else {
                        handleBrandKitClick(brandKitId);
                    }
                }}
            />
        </div>
    );

    const colorPalettesSection = !isHomeVariant && palettes.size > 0 && (
        <div className={`${shellStyles['section']} ${styles['color-palette-section']}`}>
            <div className={shellStyles['section-label']}>Color palette</div>
            <div className={styles['color-palettes-grid']}>
                {(palettes.toArray() as ImmutableMap<PaletteData>[]).map((palette, index) => {
                    const name = palette.get('name');
                    const colors = palette.get('colors') as Immutable.List<
                        ImmutableMap<TemplateColor>
                    >;
                    const hexColors = colors
                        .map((c) => int2hex(c.get('color' as TemplateColorProperties.COLOR)))
                        .toArray();
                    return (
                        <ColorPaletteTile
                            key={name}
                            name={name}
                            hexColors={hexColors}
                            selected={index === selectedPaletteIndex}
                            onClick={() => handlePaletteClick(name)}
                        />
                    );
                })}
            </div>
        </div>
    );

    const collapsedSceneCount =
        selectedAspectRatio === AspectRatio.VERTICAL ||
        selectedAspectRatio === AspectRatio.FOURFIVE ||
        selectedAspectRatio === AspectRatio.SQUARE
            ? 4
            : 3;

    const scenePreviewsBlock = isHydrated && templateId && (
        <SentryErrorBoundary fallback={<ScenePreviewsErrorFallback />}>
            <TemplateScenePreviews
                templateId={templateId}
                aspectRatio={selectedAspectRatio}
                isVisible={isVisible}
                selectedPaletteIndex={selectedPaletteIndex}
                selectedBrandKitId={selectedBrandKitId}
                maxScenes={!showAllScenes ? collapsedSceneCount : undefined}
            />
        </SentryErrorBoundary>
    );

    const showAllScenesButton = isHydrated && !showAllScenes && (
        <button
            type="button"
            className={`${styles['show-all-scenes']} mt-l5-12`}
            onClick={() => setShowAllScenes(true)}
        >
            Preview all scenes
        </button>
    );

    const altWorkflowsBlock = isHomeVariant ? (
        canUseSketch ? (
            <div className={shellStyles['alt-workflows']}>
                <span className={shellStyles['alt-workflows-label']}>Alternative workflows:</span>
                <span className={shellStyles['alt-workflows-actions']}>
                    {onStartFromScratch && (
                        <>
                            <PrimaryTextButton
                                text="Start from scratch"
                                width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                                onClick={handleStartFromScratch}
                                disabled={isProceeding}
                            />
                            <span>or</span>
                        </>
                    )}
                    <PrimaryTextButton
                        text="start by generating a script"
                        width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                        onClick={handleUserSelection}
                        disabled={isProceeding}
                    />
                </span>
            </div>
        ) : (
            <PrimaryButton
                text="Use guided script flow"
                onClick={handleUserSelection}
                disabled={isProceeding}
            />
        )
    ) : null;

    const controls = (
        <>
            {(isHomeVariant || isPublicVariant) && (
                <div className={shellStyles['header']}>
                    Create a video using {templateName ?? 'this template'}
                </div>
            )}
            <div className={styles['format-brandkit-row']}>
                {formatSection}
                {brandKitSection}
            </div>
            {colorPalettesSection}
            {templateDescription && (
                <p className={styles['template-description']}>{templateDescription}</p>
            )}
            {isHomeVariant && canUseSketch && (
                <div className={`${shellStyles['section']} ${shellStyles['prompt-section']}`}>
                    <div className={shellStyles['section-label']}>Your video prompt</div>
                    <div className={shellStyles['prompt-wrapper']}>
                        <DashboardSketchInput
                            canUseSketch={canUseSketch}
                            canUseOutlineAnIdea={canUseOutlineAnIdea}
                            canUseAIScript={canUseAIScript}
                            hideHeader
                            hideSketchControls
                            autoFocus={isVisible}
                            onBeforeNavigate={handleSketchBeforeNavigate}
                        />
                    </div>
                </div>
            )}
        </>
    );

    return (
        <PreviewModalShell
            showing={isVisible}
            onHidden={handleModalOnClose}
            title={templateName ?? 'Preview template'}
            preview={previewBlock}
            belowPreview={
                <>
                    {scenePreviewsBlock}
                    {showAllScenesButton}
                </>
            }
            controls={controls}
            controlsFooter={altWorkflowsBlock}
            footer={isHomeVariant ? null : standardFooter}
            fixedHeight={isHomeVariant ? '68vh' : undefined}
            inline={inline}
        />
    );
});

export default TemplatePreviewModal;
